import React from 'react';
import { useTranslation } from 'react-i18next';
import { StarIcon } from '@heroicons/react/24/outline';
import { Product } from '../../../features/products/productModels';
import './ProductTabs.css';

interface Review {
  id: number;
  userName: string;
  rating: number;
  date: string;
  title: string;
  comment: string;
  verified: boolean;
  helpful: number;
}

interface ProductTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  product: Product;
  getProductName: () => string;
  getProductBrand: () => string;
  getProductModel: () => string;
  getCategoryName: () => string;
  getProductDescription: () => string;
}

const ProductTabs: React.FC<ProductTabsProps> = ({
  activeTab,
  onTabChange,
  product,
  getProductName,
  getProductBrand,
  getProductModel,
  getCategoryName,
  getProductDescription
}) => {
  const { t } = useTranslation();
  
  // Sample reviews data
  const reviews: Review[] = [];

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        className={`star ${index < Math.floor(rating) ? 'filled' : ''}`}
      />
    ));
  };

  const getAverageRating = () => {
    return reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
  };

  const getTotalReviews = () => {
    return reviews.length;
  };

  return (
    <div className="product-tabs">
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
          onClick={() => onTabChange('description')}
        >
          {t('productDetail.description')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => onTabChange('reviews')}
        >
          {t('productDetail.reviews')} ({getTotalReviews()})
        </button>
        {/* <button 
          className={`tab-button ${activeTab === 'shipping' ? 'active' : ''}`}
          onClick={() => onTabChange('shipping')}
        >
          Shipping
        </button> */}
      </div>

      <div className="tab-content">
        {activeTab === 'description' && (
          <div className="description-content">
            <h3>{t('productDetail.productDetails')}</h3>
            <p>
              {getProductDescription()}
            </p>
            <h4>{t('productDetail.keyBenefits')}</h4>
            <ul>
              {/* <li>Supports overall health and wellness</li>
              <li>Made with premium quality ingredients</li>
              <li>Third-party tested for purity and potency</li>
              <li>Easy to take with convenient serving size</li> */}
            </ul>
            {/* <h4>{t('productDetail.technicalDetails')}</h4> */}
            {product?.technicalDetails && (
              <p><strong>{t('productDetail.technicalDetails')}</strong> {product?.technicalDetails}</p>
            )}
            <p><strong>{t('productDetail.brand')}</strong> {getProductBrand()}</p>
            <p><strong>{t('productDetail.model')}</strong> {getProductModel()}</p>
            <p><strong>{t('productDetail.category')}</strong> {getCategoryName()}</p>
            {product?.description && (
              <p><strong>{t('productDetail.descriptionLabel')}</strong> {product.description}</p>
            )}
            <h4>{t('productDetail.ingredients')}</h4>
            <p>{t('productDetail.ingredientsText')}</p>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="reviews-content">
            <div className="reviews-summary">
              <div className="rating-overview">
                <div className="average-rating">
                  <span className="rating-number">{getAverageRating().toFixed(1)}</span>
                  <div className="stars">
                    {renderStars(getAverageRating())}
                  </div>
                </div>
                <p>{t('productDetail.basedOnReviews', { count: getTotalReviews() })}</p>
              </div>
            </div>

            <div className="reviews-list">
              {reviews.map(review => (
                <div key={review.id} className="review-item">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <span className="reviewer-name">{review.userName}</span>
                      {review.verified && (
                        <span className="verified-badge">{t('productDetail.verifiedPurchase')}</span>
                      )}
                    </div>
                    <div className="review-rating">
                      <div className="stars">
                        {renderStars(review.rating)}
                      </div>
                      <span className="review-date">{review.date}</span>
                    </div>
                  </div>
                  <h4 className="review-title">{review.title}</h4>
                  <p className="review-comment">{review.comment}</p>
                  <div className="review-actions">
                    <button className="helpful-button">
                      {t('productDetail.helpful')} ({review.helpful})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
{/* 
        {activeTab === 'shipping' && (
          <div className="shipping-content">
            <h3>Shipping Information</h3>
            <div className="shipping-options">
              <div className="shipping-option">
                <h4>Standard Shipping</h4>
                <p>5-7 business days - FREE on orders over ₪50</p>
                <p>Under ₪50: ₪4.99</p>
              </div>
              <div className="shipping-option">
                <h4>Express Shipping</h4>
                <p>2-3 business days - ₪9.99</p>
              </div>
              <div className="shipping-option">
                <h4>Overnight Shipping</h4>
                <p>Next business day - ₪19.99</p>
              </div>
            </div>
            <p className="shipping-note">
              We ship to over 185 countries worldwide. International shipping rates apply.
            </p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default ProductTabs;
